import React, { useCallback } from "react";
import styles from "./index.module.less";
import { Input, Tabs, message, Checkbox, Tooltip, Spin } from "antd";
import { useTranslation } from "react-i18next";
import { changeEgovUrl, crossAppSwitch } from "@/utils/tools";
import CaInputComp from "./CaInputComp";
import { isValidMobile, isValidEmail, LoginFormContext, LoginFormContextType } from "../../utils";
import GlobalContext, { GlobalContextConfig } from "@/context/GlobalContext";
import sentryManual from "@/utils/sentryManual";
import SplitLine from "../../forms/LoginForm/SplitLine";
import { CloseCircleOutlined, GlobalOutlined, LoadingOutlined } from "@/utils/antdIcon";
import AutoLoginCheckbox from "./AutoLoginCheckbox";
import { isLinuxOs } from "@/utils";
import { debounce } from "lodash";
import { getServiceAndPrivacyLinks } from "@/utils/ui";
import styles1 from "../../index.module.less";
import '../../indexNew.less'

const { TabPane } = Tabs;

const { REACT_APP_DEFAULT_COUNTRY_CODE: DEFAULT_COUNTRY_CODE = "86" } = process.env;

function MobileInputForm(props: any) {
  const { appRuntimeEnv, service, focusSDK } = React.useContext<GlobalContextConfig>(GlobalContext);
  const isWin = appRuntimeEnv.platform === "WINDOWS" || appRuntimeEnv.platform === "WEB";
  const {
    teamLoginTypeList,
    enterpriseDomain,
    onlyMobileLogin,
    setEnterpriseDomain,
    checkAndGetLoginType,
    loading,
    setLoading,
    autoLogin,
    qrcodeStatus,
    STATUS,
    onAutoLoginChange
  } = props;

  const {
    mobile,
    email,
    account,
    onLoginTypeChange,
    onMobileChange,
    onEmailChange,
    onAccountChange,
    onSendCode,
    loginType,
    loginMode,
    navigatePath
  } = React.useContext<LoginFormContextType>(LoginFormContext);

  const { t } = useTranslation("common");
  const [nextStepInstance, setNextStepInstance] = React.useState({
    valid: false,
    onClick: () => {}
  });
  const [userAgreement, setUserAgreement] = React.useState(false);

  // 防止用户频繁操作
  const debounceNextStep = debounce(() => {
    if (!isLinuxOs() && !navigator.onLine) {
      message.error("网络状态异常，请稍候再试", 6);
      return;
    }
    if (!userAgreement) {
      message.error("请先阅读并同意用户协议");
      return;
    }
    nextStepInstance.onClick();
  }, 500);

  const nextStepComp = (
    <div
      role="button"
      tabIndex={0}
      onKeyDown={() => {}}
      onClick={() => debounceNextStep()}
      className={styles1.btn}
      style={
        !loading && nextStepInstance.valid
          ? {
              opacity: 1,
              cursor: "pointer"
            }
          : {
              pointerEvents: "none",
              background: "#BABFC5"           
            }
      }
    >
      {loading && (
        <div>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 16, color: "white" }} spin />} />
        </div>
      )}
      <div
        style={{
          margin: 12,
          fontSize: 16,
          color: "rgb(255, 255, 255)"
        }}
      >
        {loginMode === "register" ? t("login.agreeAndRegister") : t("nextStep")}
      </div>
    </div>
  );

  const mobileInputComp = (
    <div className="mobileInput1">
      <div className={styles["mobile_input"]}>
        <span className={styles["mobile_input_count_code"]}>+{DEFAULT_COUNTRY_CODE}</span>
        <Input
          autoFocus
          placeholder={t("login.inputMobilePl")}
          value={mobile}
          onKeyDown={(e: any) => {
            const { keyCode } = e;
            if (keyCode === 13) {
              checkMobileCb();
            }
          }}
          onChange={(e: any) => {
            onMobileChange(e.target.value);
          }}
          allowClear
        />
      </div>
      {crossAppSwitch("selfRegistration") && (
        <div style={{ color: "#6A6A6A", marginTop: "8px" }}>若未注册，验证后将生成新账号</div>
      )}
    </div>
  );

  const emailInputComp = (
    <div className={`mobileInput1 ${styles["mobile_input"]}`}>
      <Input
        autoFocus
        placeholder={t("login.inputEmailPl")}
        value={email}
        onKeyDown={(e: any) => {
          const { keyCode } = e;
          if (keyCode === 13) {
            // 检查是否勾选
            if (!userAgreement) {
              message.error("请先阅读并同意用户协议");
              return;
            }
            sendCodeCb();
          }
        }}
        onChange={(e: any) => {
          onEmailChange(e.target.value);
        }}
        allowClear
      />
    </div>
  );
  const accountInputComp = (
    <div className="mobileInput1">
      <Input
        autoFocus
        placeholder={t("login.inputAccountPl")}
        value={account}
        onKeyDown={(e: any) => {
          const { keyCode } = e;
          if (keyCode === 13) {
            sendCodeCb();
          }
        }}
        onChange={(e: any) => {
          onAccountChange(e.target.value);
        }}
        allowClear
      />
    </div>
  );

  const checkMobileCb = React.useCallback(async () => {
    if (!userAgreement) {
      message.error("请先阅读并同意用户协议");
      return;
    }
    if (loading || !isValidMobile(mobile.split(" ").join(""))) {
      return;
    }
    setLoading(true);
    try {
      const res = await service.authService.verifyMobileLegal({
        countryCode: DEFAULT_COUNTRY_CODE,
        mobile
      });
      const { result } = res;
      if (result === "LEGAL") {
        await onSendCode();
      } else {
        message.error(t("login.invalidMobile"));
      }
    } catch (e) {
      sentryManual.captureException(e, "login-mobile");
      focusSDK.printLog("login-mobile", (e as any)?.stack || (e as any)?.message || e);
      // ignore
    }
    setLoading(false);
  }, [loading, mobile, service.authService, t, onSendCode, focusSDK, userAgreement]);

  const sendCodeCb = React.useCallback(async () => {
    if (!userAgreement) {
      message.error("请先阅读并同意用户协议");
      return;
    }
    setLoading(true);
    await onSendCode();
    setLoading(false);
  }, [onSendCode, userAgreement]);

  const showNetModal = () => {
    focusSDK.emit("showNetTable", {
      fromType: "entry"
    });
  };

  const initNextStepValid = useCallback(() => {
    let type = loginType;
    if (loginMode === "vip" && enterpriseDomain) {
      type = "DOMAIN";
    }
    switch (type) {
      case "ACCOUNT":
        setNextStepInstance({
          valid: !!account,
          onClick: sendCodeCb
        });
        break;
      case "EMAIL":
        setNextStepInstance({
          valid: !!email && isValidEmail(email),
          onClick: sendCodeCb
        });
        break;
      case "MOBILE":
        setNextStepInstance({
          valid: isValidMobile(mobile),
          onClick: checkMobileCb
        });
        break;
      case "DOMAIN":
        setNextStepInstance({
          valid: !!enterpriseDomain,
          onClick: checkAndGetLoginType
        });
        break;
      // case "CA":
      // setNextStepInstance({
      //   valid: !!certId && !!caPwd,
      //   onClick: checkCaPwd
      // });
    }
  }, [
    loginType,
    loginMode,
    enterpriseDomain,
    account,
    email,
    mobile,
    // certId,
    // caPwd,
    setNextStepInstance,
    sendCodeCb,
    checkAndGetLoginType,
    checkMobileCb
    // checkCaPwd
  ]);

  React.useEffect(() => {
    initNextStepValid();
  }, [initNextStepValid]);

  return (
    <>
      <div className={styles1.mobileFormContainer}>
        {/* <div className="tab-line" /> */}
        {loginMode === "vip" ? (
          teamLoginTypeList.length ? (
            <Tabs
              className={styles["custom-tabs"]}
              defaultActiveKey={teamLoginTypeList[0].type}
              onChange={(activeKey: string) => {
                onLoginTypeChange(activeKey);
              }}
            >
              {teamLoginTypeList.map((el: any) => {
                const { typeName, type } = el;
                switch (type) {
                  case "MOBILE":
                    return (
                      <TabPane tab={typeName} key={type}>
                        {mobileInputComp}
                      </TabPane>
                    );
                  case "EMAIL":
                    return (
                      <TabPane tab={typeName} key={type}>
                        {emailInputComp}
                      </TabPane>
                    );
                  case "ACCOUNT":
                    return (
                      <TabPane tab={typeName} key={type}>
                        {accountInputComp}
                      </TabPane>
                    );
                  default:
                    return (
                      <TabPane tab="手机号" key="MOBILE">
                        {mobileInputComp}
                      </TabPane>
                    );
                }
              })}
            </Tabs>
          ) : (
            <div className="mobileInput1">
              <Input
                autoFocus
                placeholder={t("login.inputEnterpriseDomain")}
                value={enterpriseDomain}
                onKeyDown={(e: any) => {
                  const { keyCode } = e;
                  if (keyCode === 13) {
                    checkAndGetLoginType();
                  }
                }}
                onChange={(e: any) => {
                  setEnterpriseDomain(e.target.value);
                }}
                allowClear
              />
            </div>
          )
        ) : onlyMobileLogin ? (
          mobileInputComp
        ) : (
          <Tabs
            className="custom-tabs"
            defaultActiveKey={loginType}
            activeKey={loginType}
            onChange={(activeKey: string) => {
              onLoginTypeChange(activeKey);
            }}
            //   className="login-styles"
          >
            {/* <TabPane tab="扫码登录" key="QRCODE">
            {qrcodeComp}
          </TabPane> */}
            <TabPane tab="手机号" key="MOBILE">
              {mobileInputComp}
            </TabPane>
            <TabPane tab="邮箱" key="EMAIL">
              {emailInputComp}
            </TabPane>
            {isWin && crossAppSwitch("login") && (
              <TabPane tab="CA证书" key="CA">
                <CaInputComp
                  setLoading={() => {}}
                  onSelAccount={() => {}}
                  setNextStepInstance={setNextStepInstance}
                  onAutoLoginChange={onAutoLoginChange}
                />
              </TabPane>
            )}
          </Tabs>
        )}
      </div>
      <div>{nextStepComp}</div>

      <>
        <div style={{ margin: "12px 0" }}>
          <Checkbox
            style={{ fontSize: "12px" }}
            checked={userAgreement}
            onChange={(e: any) => {
              setUserAgreement(e.target.checked);
            }}
          >
            我已阅读并同意
            <a
              className={styles.aHover}
              style={{ marginLeft: "5px", color: "@primary-color" }}
              // href={getServiceAndPrivacyLinks(service.appRuntime).service}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                window.open(getServiceAndPrivacyLinks(service.appRuntime).service);
              }}
              // target="_blank"
            >
              用户服务协议 
            </a>
            &nbsp;&
            <a
              className={styles.aHover}
              style={{ marginLeft: "5px", color: "@primary-color" }}
              //   onClick={(e) => {
              //     e.stopPropagation();
              //     e.preventDefault();

              //   }}
              target="_blank"
              href={getServiceAndPrivacyLinks(service.appRuntime).privacy}
            >
              隐私政策
            </a>
          </Checkbox>
        </div>
        {appRuntimeEnv.platform !== "WEB" && (
          <div
            className={styles1['noAccount-box']}
            style={{
              justifyContent: loginType === "CA" ? "space-between" : "left"
            }}
          >
            <AutoLoginCheckbox
              autoLogin={autoLogin}
              qrcodeStatus={qrcodeStatus}
              STATUS={STATUS}
              onAutoLoginChange={onAutoLoginChange}
              loginType={loginType}
            />
          </div>
        )}
      </>

      {/* <div style={{ textAlign: "center", position: "absolute", bottom: "30px", width: "320px" }}>
        <SplitLine title="其他功能" />
        {crossAppSwitch("netWork") && navigatePath !== "active" && (
          <div
            className={`${
              navigatePath === "step2" && loginType === "MOBILE"
                ? `${styles1["net-check"]} ${styles1["phone-tip"]}`
                : styles1["net-check"]
            }`}
            onClick={showNetModal}
          >
            <div className={styles1["net-check-btn"]}>
              <GlobalOutlined className={styles1["check-icon"]} />
              <span className={styles1['check-text']}>网络检测</span>
            </div>
          </div>
        )}
      </div> */}
      <div style={{ textAlign: "center", position: "fixed", bottom: "16px", left: "20px"}}>
        {crossAppSwitch("netWork") && navigatePath !== "active" && (
          <div
            className={`${
              navigatePath === "step2" && loginType === "MOBILE"
                ? `${styles1["net-check"]} ${styles1["phone-tip"]}`
                : styles1["net-check"]
            }`}
            onClick={showNetModal}
          >
            <div className={styles1["net-check-btn"]}>
              <GlobalOutlined className={styles1["check-icon"]} />
              <span className={styles1['check-text']}>网络检测</span>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default MobileInputForm;
